/**
 * useUrlSearchParam Hook
 *
 * Custom hook for syncing component state with URL query parameters.
 * Provides bidirectional synchronization between state and URL.
 *
 * @module domains/personnel/presentation/hooks/useUrlSearchParam
 */

import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Result returned by useUrlSearchParam hook
 */
export interface UseUrlSearchParamResult {
  /** Current value of the search parameter */
  value: string;
  /** Function to update the search parameter value */
  setValue: (newValue: string) => void;
}

/**
 * Custom hook for syncing state with URL query parameters
 *
 * Provides bidirectional synchronization:
 * - Initializes state from URL on mount
 * - Updates URL when state changes (using history.replace)
 * - Updates state when URL changes (browser back/forward)
 * - Removes param from URL when value is empty
 *
 * @param {string} paramName - Name of the query parameter to sync
 * @param {string} defaultValue - Default value if param is not in URL
 * @returns {UseUrlSearchParamResult} Current value and setter function
 *
 * @example
 * const { value: searchQuery, setValue: setSearchQuery } = useUrlSearchParam('search', '');
 *
 * @example
 * const { value: category, setValue: setCategory } = useUrlSearchParam('category', 'all');
 */
export const useUrlSearchParam = (paramName: string, defaultValue: string = ''): UseUrlSearchParamResult => {
  const history = useHistory();
  const location = useLocation();

  // ✅ Pure helper: Extract param value from URLSearchParams
  const getParamValue = (searchParams: URLSearchParams): string => {
    return searchParams.get(paramName) || defaultValue;
  };

  // ✅ Initialize state from URL on mount
  const [value, setValue] = useState<string>(() => getParamValue(new URLSearchParams(location.search)));

  // ✅ Sync state when URL changes (browser back/forward)
  useEffect(() => {
    const urlValue = getParamValue(new URLSearchParams(location.search));

    // Early return if values match
    if (urlValue === value) return;

    setValue(urlValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, paramName, defaultValue]);

  // ✅ Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentUrlValue = getParamValue(params);

    // Early return if values match
    if (value === currentUrlValue) return;

    // Update or remove param based on value
    if (value && value !== defaultValue) {
      params.set(paramName, value);
    } else {
      params.delete(paramName);
    }

    const newSearch = params.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    history.replace(newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, paramName, defaultValue]);

  return {
    value,
    setValue,
  };
};
